import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Lock, CheckCircle, AlertTriangle, Eye, EyeOff, Loader2 } from "lucide-react";
import { API } from "../lib/constants";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const uid = params.get("uid") || "";

  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verify = async () => {
      if (!token || !uid) {
        setTokenValid(false);
        setVerifying(false);
        return;
      }
      try {
        const r = await fetch(`${API}/auth/verify-reset-token`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token, uid }),
        });
        if (!r.ok) { setTokenValid(false); setVerifying(false); return; }
        const d = await r.json();
        setTokenValid(!!d.valid);
      } catch {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };
    verify();
  }, [token, uid]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, uid, new_password: password }),
      });
      if (!r.ok) {
        let msg = "Reset failed. Please request a new link.";
        try { const d = await r.json(); msg = d.detail || msg; } catch {}
        setError(msg);
        return;
      }
      setSuccess(true);
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center" data-testid="reset-loading">
        <Loader2 size={32} className="animate-spin text-red-600" />
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen pt-32 px-6 flex items-center justify-center" data-testid="reset-invalid">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
            <AlertTriangle size={40} className="text-amber-500" />
          </div>
          <h1 className="font-cinzel text-3xl font-bold mb-3">Link Invalid or Expired</h1>
          <p className="text-gray-400 mb-8 text-sm leading-relaxed">
            This password reset link has expired or has already been used. Please request a new one from the sign-in page.
          </p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-all"
            data-testid="reset-back-btn"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen pt-32 px-6 flex items-center justify-center" data-testid="reset-success">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-green-500" />
          </div>
          <h1 className="font-cinzel text-3xl font-bold mb-3">Password Updated</h1>
          <p className="text-gray-400 mb-8 text-sm leading-relaxed">
            Your password has been reset. You can now sign in with your new password.
          </p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-all"
            data-testid="reset-signin-btn"
          >
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-20 px-6 flex items-center justify-center" data-testid="reset-form-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <p className="font-mono text-red-500 text-xs tracking-[0.3em] mb-2">RED INK STUDIOS</p>
          <h1 className="font-cinzel text-3xl font-bold mb-2">Choose a New Password</h1>
          <p className="text-sm text-gray-500">Pick something strong you'll remember.</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
          <div className="relative">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type={showPass ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="New password (min 8 characters)"
              minLength={8}
              required
              className="w-full pl-10 pr-10 py-3 bg-[#1a1a1a] border border-white/15 rounded-xl text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-red-500"
              data-testid="reset-password-input"
            />
            <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
              {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <div className="relative">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type={showPass ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm new password"
              minLength={8}
              required
              className="w-full pl-10 pr-4 py-3 bg-[#1a1a1a] border border-white/15 rounded-xl text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-red-500"
              data-testid="reset-confirm-input"
            />
          </div>
          {error && <p className="text-red-400 text-sm" data-testid="reset-error">{error}</p>}
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-all disabled:opacity-50"
            data-testid="reset-submit-btn"
          >
            {submitting ? <Loader2 size={16} className="animate-spin mx-auto" /> : "Update Password"}
          </button>
        </form>
      </div>
    </div>
  );
}
